/* ----------------------------------------------------------------------------
 * Outreach P0 — owner capture pure model (no network, no import.meta).
 *
 * Deliberately DEPENDENCY-FREE so it can be unit-tested under
 * `node --test --experimental-strip-types` exactly like data/support-model.ts
 * and data/content-generate-model.ts. Types + the client-side JSON
 * normalise/parse that CapturePage renders around; the network call
 * (submitCapture, which needs the `@/lib/api` session-cookie client) lives in
 * the sibling data/capture.ts, which imports this file.
 * -------------------------------------------------------------------------- */

/** Free-text tag for how a lead was captured. `claude_chrome` = the owner's
 *  Claude-in-Chrome dogfood route (isolated profile, own-site-only); `manual`
 *  = the owner typed/pasted it by hand. Any other server-accepted string also
 *  passes through untouched. */
export type CapturedVia = 'claude_chrome' | 'manual' | string;

export const CAPTURED_VIA_OPTIONS: Array<{ value: CapturedVia; label: string }> = [
  { value: 'claude_chrome', label: 'Claude in Chrome (owner dogfood)' },
  { value: 'manual', label: 'Manual paste' },
];

export const DEFAULT_CAPTURED_VIA: CapturedVia = 'claude_chrome';

/** One captured lead row. Every field is an optional string except
 *  `captured_via` (required by the contract; the page fills a default when a
 *  pasted row omits it). */
export interface CaptureLeadInput {
  practitioner_name?: string;
  business_name?: string;
  website?: string;
  email?: string;
  phone?: string;
  instagram?: string;
  linkedin?: string;
  facebook?: string;
  other_social?: string;
  city_region?: string;
  postcode?: string;
  services_category?: string;
  personalisation_note?: string;
  source_directory?: string;
  source_url?: string;
  captured_via: string;
  notes?: string;
}

export interface CaptureEnvelope {
  leads: CaptureLeadInput[];
}

export type CaptureOutcome = 'created' | 'merged' | 'duplicate' | 'rejected';

export interface CaptureResultRow {
  outcome: CaptureOutcome;
  lead_id?: number;
  reason?: string;
}

export interface CaptureSummary {
  created: number;
  merged: number;
  duplicate: number;
  rejected: number;
}

export interface CaptureResponse {
  results: CaptureResultRow[];
  summary: CaptureSummary;
}

/** A client-side parse/shape problem — never a transport failure, so it is
 *  never an ApiError. The page renders `message` inline, next to the
 *  textarea, before anything is sent. */
export class CaptureParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CaptureParseError';
  }
}

/** Parse raw pasted text as JSON, throwing a friendly CaptureParseError (never
 *  a raw SyntaxError) on malformed input. */
export function parseCaptureText(raw: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) {
    throw new CaptureParseError('Paste some lead JSON first.');
  }
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    const detail = e instanceof Error ? e.message : 'parse error';
    throw new CaptureParseError(
      `That isn't valid JSON (${detail}). Check for a trailing comma, unmatched bracket, or unquoted key.`,
    );
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function coerceLeadRow(row: Record<string, unknown>, index: number, defaultCapturedVia: string): CaptureLeadInput {
  const lead: Record<string, string> = {};
  for (const [k, v] of Object.entries(row)) {
    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (trimmed !== '') lead[k] = trimmed;
    } else if (typeof v === 'number' || typeof v === 'boolean') {
      lead[k] = String(v);
    } else if (v !== null && v !== undefined) {
      throw new CaptureParseError(`Lead #${index + 1}, field "${k}": expected a string, got ${typeof v}.`);
    }
  }
  if (!lead.captured_via) lead.captured_via = defaultCapturedVia;
  return lead as unknown as CaptureLeadInput;
}

/**
 * Normalise a parsed JSON.parse() value into the `{leads:[...]}` envelope the
 * API expects. Accepts three input shapes so the owner (or Claude-in-Chrome)
 * can paste whatever came out naturally:
 *   - a single lead object            -> { leads: [obj] }
 *   - an array of lead objects        -> { leads: [...] }
 *   - the full envelope {leads:[...]} -> passed through (re-validated)
 * A lead missing `captured_via` gets the page's selected default so bare
 * paste-JSON without that field still submits cleanly.
 */
export function normaliseCapture(parsed: unknown, defaultCapturedVia: string): CaptureEnvelope {
  let rawLeads: unknown[];

  if (Array.isArray(parsed)) {
    rawLeads = parsed;
  } else if (isPlainObject(parsed) && Array.isArray(parsed.leads)) {
    rawLeads = parsed.leads;
  } else if (isPlainObject(parsed)) {
    rawLeads = [parsed];
  } else {
    throw new CaptureParseError(
      'Pasted JSON must be a lead object, an array of leads, or {"leads":[...]}.',
    );
  }

  if (rawLeads.length === 0) {
    throw new CaptureParseError('No leads found — the pasted JSON is empty.');
  }

  const leads = rawLeads.map((row, i) => {
    if (!isPlainObject(row)) {
      throw new CaptureParseError(`Lead #${i + 1} is not a JSON object.`);
    }
    return coerceLeadRow(row, i, defaultCapturedVia);
  });

  return { leads };
}

export function summariseCaptureResults(results: CaptureResultRow[]): CaptureSummary {
  const summary: CaptureSummary = { created: 0, merged: 0, duplicate: 0, rejected: 0 };
  for (const r of results) {
    if (r.outcome in summary) summary[r.outcome] += 1;
  }
  return summary;
}

/** Honest, mixed-outcome summary sentence for the results panel — never a
 *  blanket "success". e.g. "2 new leads, 3 merged into existing, 1 duplicate
 *  skipped, 1 rejected." Omits any zero-count clause. */
export function describeCaptureSummary(summary: CaptureSummary): string {
  const parts: string[] = [];
  if (summary.created > 0) parts.push(`${summary.created} new ${summary.created === 1 ? 'lead' : 'leads'}`);
  if (summary.merged > 0) parts.push(`${summary.merged} merged into existing`);
  if (summary.duplicate > 0) parts.push(`${summary.duplicate} duplicate ${summary.duplicate === 1 ? 'skipped' : 'skipped'}`);
  if (summary.rejected > 0) parts.push(`${summary.rejected} rejected`);
  if (parts.length === 0) return 'No leads were submitted.';
  return parts.join(', ') + '.';
}
